/* eslint-disable no-plusplus */
import { pick, extend, chain, isNumber, result, isString, map, each, isEmpty, clone, negate, isUndefined, pluck, difference, findWhere } from 'underscore';
import Promise from 'bluebird';
import { AssetGroup, AudienceGroupAssetGroupAssociation } from '@bingads-webui/campaign-odata-schemas';
import { createOrUpdateAssetGroup, readAssetGroup } from '@bingads-webui-campaign-react/perf-max-data';
import { processImageListViewModelToModel } from '@bingads-webui-campaign-react/pmax-image-experience';
import { fromViewModelToBulkModel as viewModelToBulkModelForListingGroup } from '@bingads-webui-campaign-react/listing-groups-page';
import { isEmptyUrl } from '@bingads-webui-campaign-react/url-input-fluent';
import { getUrlRulesOption, URL_RULES_OPTIONS } from '@bingads-webui-campaign-react/url-rules';
import { newVideoFromMTObject } from '@bingads-webui-campaign-react/image-experience-video-part';
import { getImagesFromMTAssetsAsync } from '@bingads-webui-campaign-react/responsive-ad-editor-utils';
import odata from '@bingads-webui-component-legacy/http/channel/odata';
import { initialAssetGroupData, AssetGroupNegativeReferenceId } from '../common/constants';
import { isBulkWrappedResponse } from '../common/bulk';
import { getBulkUploadErrorType, hasBulkErrorsForEntityType, isAudienceAssociationDoesntExistError, modelToViewModelForBulkErrors, removeErrors, removeReferencedEntityInvalidIdErrors, unexpectedBulkErrorToString } from './bulk-upsert-errors';
import { ERROR_ENTITY, genericApiError } from './api-errors';

const globalLegacyI18n = window.globalLegacyI18n.get('@bingads-webui/i18n-amd-loader!campaignui/campaign-creation-advanced');

const bulkUpsertAssetGroupSettingsEntityErrorMap = {
  [getBulkUploadErrorType(AssetGroup)]: ERROR_ENTITY.AssetGroup,
  [getBulkUploadErrorType(AudienceGroupAssetGroupAssociation)]: ERROR_ENTITY.AudienceGroupAssetGroupAssociation,
};

export const initialAssetGroupViewModel = {
  data: initialAssetGroupData,
  isValid: false,
  isDirty: false,
};

export const getErrorsPlaceholders = () => ({
  serverErrors: [],
  systemErrors: [],
});


const whitelistedSchemaPropertyKeysForBulkModel = [
  'Headlines', 'LongHeadlines', 'Descriptions', 'Images', 'Campaign', 'Id', 'Name', 'Status',
  'Path1', 'Path2', 'BusinessName', 'CallToAction', 'FinalUrls', 'MobileFinalUrls', 'StartDate', 'EndDate',
  'AssetGroupSearchThemes', 'AssetGroupUrlTargets', 'Videos',
];

const whitelistedSchemaPropertyKeysForModel = ['AudienceGroup', 'UrlRulesOption'].concat(whitelistedSchemaPropertyKeysForBulkModel);

const whitelistedSchemaPropertyKeys = whitelistedSchemaPropertyKeysForModel;

const assetsArrayToObject = (assetsArray, propertyPrefix) => {
  const assetObject = {};

  each(assetsArray, (asset, index) => {
    const assetText = chain(asset).result('Asset', {}).result('Text', '').value();
    assetObject[`${propertyPrefix}${index}`] = assetText;
  });

  return assetObject;
};

const modelToViewModelForTextAssets = (assetGroupModel, propertyName, propertyPrefix) => {
  const assetsArray = result(assetGroupModel, propertyName, []);
  const viewModelOverride = {};
  viewModelOverride[`${propertyName}`] = assetsArrayToObject(assetsArray, propertyPrefix);
  return viewModelOverride;
};

const modelToViewModelForSearchThemes = (assetGroupModel, assetGroupViewModel) => {
  const searchThemes = result(assetGroupViewModel.data, 'AssetGroupSearchThemes', []);
  const newResult = [];

  assetGroupModel.AssetGroupSearchThemes.forEach((item) => {
    const index = searchThemes.findIndex(element => element.SearchTheme === item.SearchTheme);
    if (item.Status === 'Deleted') {
      searchThemes.splice(index, 1);
    } else {
      if (index >= 0) {
        searchThemes.splice(index, 1);
      }
      newResult.push(item);
    }
  });

  return { AssetGroupSearchThemes: searchThemes.concat(newResult) };
};

const bulkModelToViewModelForUrlRules = (assetGroupBulkModel, assetGroupViewModel) => {
  const urlRulesOption = result(assetGroupViewModel.data, 'UrlRulesOption', URL_RULES_OPTIONS.All);
  if (urlRulesOption === URL_RULES_OPTIONS.All) {
    return { AssetGroupUrlTargets: [] };
  }

  const urlTargets = result(assetGroupViewModel.data, 'AssetGroupUrlTargets', []);
  const bulkResponse = result(assetGroupBulkModel, 'AssetGroupUrlTargets', []);
  const resultsWithIds = map(urlTargets, (item) => {
    if (item.Id) return item;
    const target = bulkResponse.filter(targetItem => targetItem.Status !== 'Deleted'
      && targetItem.Condition1 === item.TargetCondition1
      && targetItem.Value1 === item.TargetValue1
      && targetItem.Value2 === item.TargetValue2
      && targetItem.Value3 === item.TargetValue3);
    return { ...item, Id: target.length ? target[0].Id : undefined };
  });

  return { AssetGroupUrlTargets: resultsWithIds, UrlRulesOption: getUrlRulesOption(resultsWithIds) };
};

const bulkModelToViewModelForUrlRulesV2 = (assetGroupBulkModel, assetGroupViewModel) => {
  const urlTargets = result(assetGroupViewModel.data, 'AssetGroupUrlTargets', []);
  const bulkResponse = result(assetGroupBulkModel, 'AssetGroupUrlTargets', []);
  const resultsWithIds = map(urlTargets, (item) => {
    if (item.Id) return item;
    const target = bulkResponse.filter(targetItem => targetItem.Status !== 'Deleted'
      && targetItem.Condition1 === item.TargetCondition1
      && targetItem.Condition2 === item.TargetCondition2
      && targetItem.Condition3 === item.TargetCondition3
      && targetItem.Value1 === item.TargetValue1
      && targetItem.Value2 === item.TargetValue2
      && targetItem.Value3 === item.TargetValue3);
    return { ...item, Id: target.length ? target[0].Id : undefined };
  });

  return { AssetGroupUrlTargets: resultsWithIds };
};

const modelToViewModelForAudienceGroup = (assetGroupModel, assetGroupViewModel) => {
  const associatedIds = map(assetGroupModel.AudienceGroupAssetGroupAssociations, association => association.AudienceGroupId);

  const audienceGroup = result(assetGroupViewModel.data, 'AudienceGroup', []);

  // deleted audience group
  if (!audienceGroup) {
    return { AudienceGroup: null };
  }

  const associatedAudienceGroup = associatedIds.includes(audienceGroup.Id) ? audienceGroup : null;

  if (associatedAudienceGroup) {
    return {
      AudienceGroup: associatedAudienceGroup,
    };
  }

  return {};
};

const viewModelToBulkModelForSearchThemes = (assetGroupViewModel) => {
  const { AssetGroupSearchThemes, Id = AssetGroupNegativeReferenceId } = assetGroupViewModel;
  const searchThemesresult = [];
  AssetGroupSearchThemes.forEach((item) => {
    if (item.Status === 'Deleted') {
      searchThemesresult.push({
        AssetGroupId: Id,
        SearchTheme: item.SearchTheme,
        AssetGroupSearchThemeId: item.Id,
        Status: item.Status,
      });
    } else if (!item.Id) {
      searchThemesresult.push({
        AssetGroupId: Id,
        SearchTheme: item.SearchTheme,
      });
    }
  });
  return { AssetGroupSearchThemes: searchThemesresult };
};

const viewModelToBulkModelForUrlRules = (assetGroupViewModel, originalAssetGroupViewModel, serverContext) => {
  const isAssetGroupUrlRulesV2Enabled = serverContext?.DynamicPermissions?.AssetGroupUrlRulesV2 || false;
  const { AssetGroupUrlTargets: newAssetGroupUrlTargets, Id = AssetGroupNegativeReferenceId } = assetGroupViewModel;

  if (!isAssetGroupUrlRulesV2Enabled) {
    const { UrlRulesOption } = assetGroupViewModel;

    if (UrlRulesOption === URL_RULES_OPTIONS.All) {
      const deletedTargets = newAssetGroupUrlTargets.map(target => ({
        AssetGroupId: Id,
        AssetGroupUrlTargetId: target.Id,
        Status: 'Deleted',
      }));

      return {
        AssetGroupUrlTargets: deletedTargets,
      };
    }
  }

  const newTargetIds = pluck(newAssetGroupUrlTargets, 'Id');

  const mapTargetFields = (target) => {
    const bulkModel = {
      AssetGroupId: Id,
      Condition1: target.TargetCondition1,
      Operator1: target.TargetConditionOperator1,
      Value1: target.TargetValue1,
    };

    each([2, 3], (index) => {
      if (target[`TargetCondition${index}`]) {
        extend(bulkModel, {
          [`Condition${index}`]: target[`TargetCondition${index}`],
          [`Operator${index}`]: target[`TargetConditionOperator${index}`],
          [`Value${index}`]: target[`TargetValue${index}`],
        });
      }
    });

    return bulkModel;
  };

  let deletedTargets = [];
  if (originalAssetGroupViewModel) {
    const { AssetGroupUrlTargets: originalAssetGroupUrlTargets } = originalAssetGroupViewModel;
    const originalTargetIds = pluck(originalAssetGroupUrlTargets, 'Id');
    const deletedTargetIds = difference(originalTargetIds, newTargetIds);
    deletedTargets = deletedTargetIds.map((id) => {
      const target = findWhere(originalAssetGroupUrlTargets, { Id: id });
      return {
        AssetGroupId: Id,
        AssetGroupUrlTargetId: target.Id,
        Status: 'Deleted',
      };
    });
  }

  const newTargets = newAssetGroupUrlTargets.filter(target => !target.Id).map(target => mapTargetFields(target));

  return {
    AssetGroupUrlTargets: [
      ...deletedTargets,
      ...newTargets,
    ],
  };
};

const prepareViewModelForWindowsImageFetch = (assetGroupModel, cid, aid) => {
  const images = {
    ImageSPOTLIGHT: {
      ImageAssets: null,
      ImageList: null,
    },
    ImagePRONG: {
      ImageAssets: null,
      ImageList: null,
    },
    TransparentLogo: {
      ImageAssets: null,
      ImageList: null,
    },
  };
  if (!assetGroupModel || !assetGroupModel.Images) {
    return images;
  }
  const imageProngModel = assetGroupModel.Images.find(x => x.AssociationType === 'ImagePRONG');
  const imageSpotLightModel = assetGroupModel.Images.find(x => x.AssociationType === 'ImageSPOTLIGHT');
  const transparentLogoModel = assetGroupModel.Images.find(x => x.AssociationType === 'TransparentLogo');

  const imageProngVMPromise = imageProngModel
    ? getImagesFromMTAssetsAsync(odata, `/Customers(${cid})/Accounts(${aid})/` /* baseUrl */, [imageProngModel], false, false, false, true, false)
    : Promise.resolve([]);
  const imageSpotLightVMPromise = imageSpotLightModel
    ? getImagesFromMTAssetsAsync(odata, `/Customers(${cid})/Accounts(${aid})/` /* baseUrl */, [imageSpotLightModel], false, false, false, false, true)
    : Promise.resolve([]);
  const transparentLogoVMPromise = transparentLogoModel
    ? getImagesFromMTAssetsAsync(odata, `/Customers(${cid})/Accounts(${aid})/` /* baseUrl */, [transparentLogoModel], false, false, false, false, false, true)
    : Promise.resolve([]);

  return imageProngVMPromise.then(imageProngVM => imageSpotLightVMPromise.then(imageSpotLightVM => transparentLogoVMPromise.then(transparentLogoVM => ({
    ImageSPOTLIGHT: {
      ImageAssets: null,
      ImageList: imageSpotLightVM,
    },
    ImagePRONG: {
      ImageAssets: null,
      ImageList: imageProngVM,
    },
    TransparentLogo: {
      ImageAssets: null,
      ImageList: transparentLogoVM,
    },
  }))));
};

const prepareViewModelForImageFetch = assetGroupModel => ({
  Images: {
    ImageAssets: result(assetGroupModel, 'Images', []),
  },
});

// keeping unused i18n in case we find bugs with replacement to globalLegacyI18n and change need to be reverted
function parseLoadDate(i18n, dateString) {
  return globalLegacyI18n.parseDate(dateString.substring(0, 10), { raw: 'yyyy-MM-dd' });
}

const modelToViewModelForDateSetting = ({ assetGroupModel, i18n }) => ({
  StartDate: parseLoadDate(i18n, assetGroupModel.StartDate),
  EndDate: assetGroupModel.EndDate ? parseLoadDate(i18n, assetGroupModel.EndDate) : undefined,
});

const modelToViewModelForURLRules = (assetGroupModel, isUrlRulesV2Enabled) => {
  const assetGroupUrlTargets = result(assetGroupModel, 'AssetGroupUrlTargets', []);
  return isUrlRulesV2Enabled ? {} : { UrlRulesOption: getUrlRulesOption(assetGroupUrlTargets) };
};

const modelToViewModelForAssetGroup = (assetGroupModel, i18n, isUrlRulesV2Enabled) => {
  let data = pick(assetGroupModel, whitelistedSchemaPropertyKeys);
  data = extend(data, modelToViewModelForTextAssets(assetGroupModel, 'Headlines', 'Headline'));
  data = extend(data, modelToViewModelForTextAssets(assetGroupModel, 'LongHeadlines', 'LongHeadline'));
  data = extend(data, modelToViewModelForTextAssets(assetGroupModel, 'Descriptions', 'Description'));
  data = extend(data, prepareViewModelForImageFetch(assetGroupModel));

  data = extend(data, modelToViewModelForDateSetting({
    assetGroupModel,
    i18n,
  }));

  data = extend(data, modelToViewModelForURLRules(assetGroupModel, isUrlRulesV2Enabled));

  const assetGroupViewModel = {
    data,
    isValid: true,
    isDirty: false,
  };
  return assetGroupViewModel;
};

// keeping unused i18n in case we find bugs with replacement to globalLegacyI18n and change need to be reverted
const formatDateToAPIFormat = (i18n, date) => {
  if (date == null) {
    return '';
  }
  return globalLegacyI18n.formatDate(date, {
    raw: 'M/d/yyyy',
  });
};

// keeping unused i18n in case we find bugs with replacement to globalLegacyI18n and change need to be reverted
const parseDateFromAPIFormat = (i18n, date) => {
  if (date == null) {
    return null;
  }

  if (typeof date === 'string') {
    return globalLegacyI18n.parseDate(date.substring(0, 10), { raw: 'M/d/yyyy' });
  }

  return date;
};

const viewModelToBulkModelForDateSetting = (i18n, assetGroupViewModel) => ({
  StartDate: formatDateToAPIFormat(i18n, assetGroupViewModel.data.StartDate),
  EndDate: assetGroupViewModel.data.EndDate ? formatDateToAPIFormat(i18n, assetGroupViewModel.data.EndDate) : '1/1/3000',
});

const bulkModelToViewModelForDateSetting = ({ assetGroupModel, i18n }) => ({
  StartDate: parseDateFromAPIFormat(i18n, assetGroupModel.StartDate),
  EndDate: assetGroupModel.EndDate ? parseDateFromAPIFormat(i18n, assetGroupModel.EndDate) : undefined,
});

const createBulkModelForListingGroup = (listingGroups) => {
  const hasProductFilters = listingGroups && listingGroups.items && listingGroups.items.length > 0;

  const rootNodeId = -1;
  let negativeId = -2;
  let bulkModel = [{
    Id: rootNodeId,
    AssetGroupId: AssetGroupNegativeReferenceId,
    IsExcluded: false,
    IsLeaf: !hasProductFilters,
    Condition: {
      Operand: 'All',
      Attribute: '',
    },
  }];
  if (hasProductFilters) {
    const dummyEveryThingElse = {
      key: '_DUMMY_EVERY_THING_ELSE',
      data: { Attribute: '' },
    };

    let finalListingGroups = [
      ...listingGroups.items,
      dummyEveryThingElse,
    ];
    finalListingGroups = finalListingGroups.map(item => ({
      Id: negativeId--,
      IsExcluded: item.key === dummyEveryThingElse.key,
      ParentId: rootNodeId,
      IsLeaf: true,
      AssetGroupId: AssetGroupNegativeReferenceId,
      Condition: { Attribute: item.data.Attribute, Operand: listingGroups.operand },
    }));

    bulkModel = bulkModel.concat(map(finalListingGroups, listingGroup => viewModelToBulkModelForListingGroup(listingGroup)));
  }

  return {
    ProductListingGroups: bulkModel,
  };
};

const textAssetPropertyNameRegEx = /([^\d]+)(\d+)/;
const parseAssetPropertyName = (propertyName) => {
  const matches = propertyName.match(textAssetPropertyNameRegEx);
  return matches !== null ?
    ({ name: matches[1], index: parseInt(matches[2], 10) }) :
    ({ name: 'invalid property', index: NaN });
};

const assetsObjectToArray = (assetsObject, propertyPrefix) =>
  chain(assetsObject)
    .pairs()
    .filter(([, value]) => !isEmpty(value))
    .map(([key, value]) => [parseAssetPropertyName(key), value])
    .filter(([{ name, index }]) => name === propertyPrefix && isNumber(index))
    .value();

const assetToModel = ([, value]) => ({
  Asset: {
    Text: value,
  },
});

const viewModelToBulkModelForTextAssets = (assetGroupViewModel, propertyName, vmPropertyPrefix) => ({
  [propertyName]:
    assetsObjectToArray(result(assetGroupViewModel, propertyName, {}), vmPropertyPrefix)
      .map(assetToModel),
});

const viewModelToBulkModelForImages = (assetGroupViewModel, serverContext, aid, cid) => {
  const { ImageList = [] } = assetGroupViewModel.Images;

  if (assetGroupViewModel.ImageSPOTLIGHT && assetGroupViewModel.ImageSPOTLIGHT.ImageList) {
    ImageList.push(...assetGroupViewModel.ImageSPOTLIGHT.ImageList);
  }
  if (assetGroupViewModel.ImagePRONG && assetGroupViewModel.ImagePRONG.ImageList) {
    ImageList.push(...assetGroupViewModel.ImagePRONG.ImageList);
  }
  if (assetGroupViewModel.TransparentLogo && assetGroupViewModel.TransparentLogo.ImageList) {
    ImageList.push(...assetGroupViewModel.TransparentLogo.ImageList);
  }
  const model = processImageListViewModelToModel(ImageList, serverContext, aid, cid);
  return model;
};

const isAudienceSignalAssetGroupAssociationCreateOrUpdateScenario = assetGroupViewModel =>
  !!result(assetGroupViewModel.AudienceGroup, 'Id');
const isAudienceSignalAssetGroupAssociationDeleteScenario = (assetGroupViewModel, originalAssetGroupViewModel) =>
  !isAudienceSignalAssetGroupAssociationCreateOrUpdateScenario(assetGroupViewModel) &&
  !!result(originalAssetGroupViewModel?.AudienceGroup, 'Id');


const viewModelToBulkModelForAudienceGroup = (assetGroupViewModel, originalAssetGroupViewModel) => {
  const { AudienceGroup, Id = AssetGroupNegativeReferenceId } = assetGroupViewModel;
  let model = {};
  if (isAudienceSignalAssetGroupAssociationCreateOrUpdateScenario(assetGroupViewModel)) {
    model = extend({
      AudienceGroupAssetGroupAssociations: [{
        AudienceGroupId: AudienceGroup.Id,
        AssetGroupId: Id,
        Status: 'Active',
      }],
    });
  } else if (isAudienceSignalAssetGroupAssociationDeleteScenario(assetGroupViewModel, originalAssetGroupViewModel)) {
    const { AudienceGroup: OriginalAudienceGroup } = originalAssetGroupViewModel;
    model = extend({
      AudienceGroupAssetGroupAssociations: [{
        AudienceGroupId: OriginalAudienceGroup.Id,
        AssetGroupId: Id,
        Status: 'Deleted',
      }],
    });
  }
  return model;
};

const viewModelToBulkModelForFinalUrls = (assetGroupViewModel) => {
  const urls = result(assetGroupViewModel, 'FinalUrls', null);
  if (!urls || isEmptyUrl(urls)) {
    return { FinalUrls: [] };
  }

  return { FinalUrls: urls };
};

const viewModelToBulkModelForMobileFinalUrls = (assetGroupViewModel) => {
  const isCreation = !(assetGroupViewModel.Id > 0);
  const urls = result(assetGroupViewModel, 'MobileFinalUrls', null);
  if (!urls || isEmptyUrl(urls)) {
    if (isCreation) {
      return { MobileFinalUrls: '' };
    }

    return { MobileFinalUrls: 'delete_value' };
  }

  return { MobileFinalUrls: urls.join(';') };
};

const viewModelToBulkModelForAssetGroup = (i18n, serverContext, cid, aid, assetGroupViewModel, originalAssetGroup, campaign) => {
  let model = pick(assetGroupViewModel.data, whitelistedSchemaPropertyKeysForBulkModel);
  if (!assetGroupViewModel.data.Id) {
    model.Id = AssetGroupNegativeReferenceId;
  }
  if (!assetGroupViewModel.data.Id && campaign && campaign.CampaignSettings && campaign.CampaignSettings.ProviderId) {
    model = extend(model, createBulkModelForListingGroup(assetGroupViewModel.data.listingGroup));
  }
  model = extend(model, viewModelToBulkModelForDateSetting(i18n, assetGroupViewModel));
  model = extend(model, viewModelToBulkModelForTextAssets(assetGroupViewModel.data, 'Headlines', 'Headline'));
  model = extend(model, viewModelToBulkModelForTextAssets(assetGroupViewModel.data, 'LongHeadlines', 'LongHeadline'));
  model = extend(model, viewModelToBulkModelForTextAssets(assetGroupViewModel.data, 'Descriptions', 'Description'));

  if (assetGroupViewModel.data.AudienceGroup || originalAssetGroup.data?.AudienceGroup) {
    model = extend(model, viewModelToBulkModelForAudienceGroup(assetGroupViewModel.data, originalAssetGroup.data));
  }

  model = extend(model, viewModelToBulkModelForFinalUrls(assetGroupViewModel.data));
  model = extend(model, viewModelToBulkModelForMobileFinalUrls(assetGroupViewModel.data));

  if (assetGroupViewModel.data.AssetGroupSearchThemes || originalAssetGroup.data?.AssetGroupSearchThemes) {
    model = extend(model, viewModelToBulkModelForSearchThemes(assetGroupViewModel.data, 'AssetGroupSearchTheme', 'AssetGroupSearchTheme'));
  }

  if (assetGroupViewModel.data.AssetGroupUrlTargets || originalAssetGroup.data?.AssetGroupUrlTargets) {
    model = extend(model, viewModelToBulkModelForUrlRules(assetGroupViewModel.data, originalAssetGroup.data, serverContext));
  }

  if (assetGroupViewModel.data.videos && Array.isArray(assetGroupViewModel.data.videos)) {
    const videos = assetGroupViewModel.data.videos.map(video => ({
      id: video.id,
      idString: video.idString,
      subType: video.type,
    }));

    model = extend(model, { Videos: JSON.stringify(videos) });
  }

  return viewModelToBulkModelForImages(assetGroupViewModel.data, serverContext, cid, aid)
    .then(imagesModel => extend(model, imagesModel));
};

export const viewModelToModelForAssetGroup = (assetGroupViewModel) => {
  const model = pick(assetGroupViewModel.data, whitelistedSchemaPropertyKeysForModel);
  if (!assetGroupViewModel.data.Id) {
    model.Id = AssetGroupNegativeReferenceId;
  }
  return model;
};

export const modelToViewModelForWindowsAssetGroup = (assetGroupModel, i18n, cid, aid) => {
  let data = pick(assetGroupModel, whitelistedSchemaPropertyKeys);
  data = extend(data, modelToViewModelForTextAssets(assetGroupModel, 'Headlines', 'Headline'));
  data = extend(data, modelToViewModelForTextAssets(assetGroupModel, 'LongHeadlines', 'LongHeadline'));
  data = extend(data, modelToViewModelForTextAssets(assetGroupModel, 'Descriptions', 'Description'));

  data = extend(data, modelToViewModelForDateSetting({
    assetGroupModel,
    i18n,
  }));

  return prepareViewModelForWindowsImageFetch(assetGroupModel, cid, aid).then((image) => {
    data = extend(data, image);
    const assetGroupViewModel = {
      data,
      isValid: true,
      isDirty: false,
    };
    return assetGroupViewModel;
  });
};

export const loadAssetGroup = (cid, aid, campaignId, assetGroupId, i18n, isSearchThemeEnabled, isUrlRulesEnabled, isUrlRulesV2Enabled) =>
  readAssetGroup(cid, aid, campaignId, assetGroupId, isSearchThemeEnabled, isUrlRulesEnabled).then(assetGroupModel => modelToViewModelForAssetGroup(assetGroupModel, i18n, isUrlRulesV2Enabled));

export const loadAssetGroupForWindowsStore = (cid, aid, campaignId, assetGroupId, i18n) =>
  readAssetGroup(cid, aid, campaignId, assetGroupId, false, false).then(assetGroupModel => modelToViewModelForWindowsAssetGroup(assetGroupModel, i18n, cid, aid));

export const loadAssetGroupWithVideos = (cid, aid, campaignId, assetGroupId, i18n, isSearchThemeEnabled, isUrlRulesEnabled, videoRepository) => loadAssetGroup(cid, aid, campaignId, assetGroupId, i18n, isSearchThemeEnabled, isUrlRulesEnabled).then((assetGroup) => {
  const { Videos } = assetGroup && assetGroup.data;

  if (Videos) {
    const videoPromises = Promise.map(Videos, video => videoRepository.getVideo({ id: video.Asset.Id }));

    return videoPromises.then((videos) => {
      const videoObjects = map(videos, (video) => {
        const videoMT = newVideoFromMTObject(video);

        const associationType = Videos.find(v => v.Asset.Id === video.Id).AssociationType;
        return {
          ...videoMT,
          type: associationType,
          ThumbnailUrl: videoMT.thumbnailUrl,
        };
      });

      return {
        ...assetGroup,
        data: {
          ...assetGroup.data,
          videos: videoObjects,
        },
      };
    });
  }

  return assetGroup;
});

const bulkModelToViewModelForAssetGroup = ({
  i18n, scenario, activityLogger,
  assetGroupViewModel, // the asset group's view model the user tried to save
  originalAssetGroupViewModel, // the asset group's view model before the save attempt
  assetGroupModel, // the asset group's model as was generated off of assetGroupViewModel and processed by bulk infrastructure
  serverContext,
  errors = [], // the bulk upsert errors
}) => {
  let data;
  let newOriginalAssetGroupViewModel;

  const hasAssetGroupErrors = hasBulkErrorsForEntityType(errors, getBulkUploadErrorType(AssetGroup));
  const hasAudienceSignalAssetGroupAssociationErrors = hasBulkErrorsForEntityType(errors, getBulkUploadErrorType(AudienceGroupAssetGroupAssociation), negate(isAudienceAssociationDoesntExistError));
  if (!hasAssetGroupErrors) {
    data = pick(assetGroupModel, whitelistedSchemaPropertyKeys);
    // Correct the type of the AssetGroup's ID:
    if (data.Id && isString(data.Id)) {
      // eslint-disable-next-line no-param-reassign
      data.Id = parseInt(data.Id, 10);
    }
    data = extend(data, modelToViewModelForTextAssets(assetGroupModel, 'Headlines', 'Headline'));
    data = extend(data, modelToViewModelForTextAssets(assetGroupModel, 'LongHeadlines', 'LongHeadline'));
    data = extend(data, modelToViewModelForTextAssets(assetGroupModel, 'Descriptions', 'Description'));
    data = extend(data, bulkModelToViewModelForDateSetting({ assetGroupModel, i18n }));

    if (assetGroupModel.AudienceGroupAssetGroupAssociations) {
      data = extend(data, modelToViewModelForAudienceGroup(assetGroupModel, assetGroupViewModel));
    }
    if (assetGroupModel.AssetGroupSearchThemes) {
      data = extend(data, modelToViewModelForSearchThemes(assetGroupModel, assetGroupViewModel));
    }
    if (assetGroupModel.MobileFinalUrls) {
      data.MobileFinalUrls = assetGroupModel.MobileFinalUrls === 'delete_value' ? [] : [assetGroupModel.MobileFinalUrls];
    }
    if (assetGroupModel.AssetGroupUrlTargets) {
      const isAssetGroupUrlRulesV2Enabled = serverContext?.DynamicPermissions?.AssetGroupUrlRulesV2 || false;
      data = extend(
        data,
        isAssetGroupUrlRulesV2Enabled
          ? bulkModelToViewModelForUrlRulesV2(assetGroupModel, assetGroupViewModel)
          : bulkModelToViewModelForUrlRules(assetGroupModel, assetGroupViewModel)
      );
    }
    newOriginalAssetGroupViewModel = clone(assetGroupViewModel);
  } else {
    ({ data } = assetGroupViewModel);
    newOriginalAssetGroupViewModel = originalAssetGroupViewModel;

    // If AsetGroup change was not accepted, but audience signal association change was, then
    // we need to update the model to reflect the new state of audience signal association or, otherwise,
    // when the user was removing the association MT will return the error that the association is not found and the user will be blocked from saving the changes.
    // For the association creation scenario, MT doesn't generate errors if we request the create an association that already exist.
    if (!hasAudienceSignalAssetGroupAssociationErrors) {
      if (isAudienceSignalAssetGroupAssociationDeleteScenario(assetGroupViewModel.data, originalAssetGroupViewModel.data)) {
        delete newOriginalAssetGroupViewModel.data.AudienceGroup;
      } else if (isAudienceSignalAssetGroupAssociationCreateOrUpdateScenario(assetGroupViewModel.data)) {
        newOriginalAssetGroupViewModel.data.AudienceGroup = assetGroupViewModel.data.AudienceGroup;
      }
    }
  }

  data = extend(data, pick(assetGroupViewModel.data, 'Images', 'FinalUrlForRecommendation', 'videos'));

  const newAssetGroupViewModel = {
    data,
    isValid: true,
    isDirty: false,
  };

  let filteredErrors = errors;
  if (newAssetGroupViewModel.data.Id <= 0 || isUndefined(newAssetGroupViewModel.data.Id)) {
    // Handling the common creation scenario where the main entity didn't get created. In this case all related entities will not be created as well and will generate errors.
    filteredErrors = removeReferencedEntityInvalidIdErrors(errors);
  }
  // Handling the concurrency scenario where while the user was deleting the association somebody else already deleted it
  filteredErrors = removeErrors(filteredErrors, isAudienceAssociationDoesntExistError);

  const serverErrors = modelToViewModelForBulkErrors(
    filteredErrors,
    bulkUpsertAssetGroupSettingsEntityErrorMap,
    scenario,
    i18n,
    activityLogger
  );

  return { assetGroupViewModel: newAssetGroupViewModel, originalAssetGroupViewModel: newOriginalAssetGroupViewModel, serverErrors };
};

export const saveAssetGroupAndLinkedData = (
  {
    assetGroup: assetGroupViewModel,
    originalAssetGroup: originalAssetGroupViewModel,
    campaign,
  },
  {
    scenario,
    i18n,
    activity,
    activityLogger,
    serverContext,
    cid,
    aid,
  }
) => viewModelToBulkModelForAssetGroup(i18n, serverContext, cid, aid, assetGroupViewModel, originalAssetGroupViewModel, campaign)
  .then((bulkModel) => {
    const modelToSave = {
      ...bulkModel,
      Campaign: pick(campaign, 'Id'),
    };
    return createOrUpdateAssetGroup(modelToSave);
  })
  .then((assetGroupModel) => {
    const { assetGroupViewModel: assetGroup, originalAssetGroupViewModel: originalAssetGroup } = bulkModelToViewModelForAssetGroup({
      i18n, scenario, activityLogger, assetGroupViewModel, originalAssetGroupViewModel, assetGroupModel, serverContext,
    });
    const data = {
      assetGroup,
      originalAssetGroup,
      ...getErrorsPlaceholders(),
    };

    if (activity) {
      activity.signal('onAssetGroupCreationSucceeded');
    }

    return data;
  }, (bulkOrSystemError) => {
    if (isBulkWrappedResponse(bulkOrSystemError)) {
      const { entity: assetGroupModel, errors } = bulkOrSystemError;
      const { assetGroupViewModel: assetGroup, originalAssetGroupViewModel: originalAssetGroup, serverErrors } = bulkModelToViewModelForAssetGroup({
        i18n,
        scenario,
        activityLogger,
        assetGroupViewModel,
        originalAssetGroupViewModel,
        assetGroupModel,
        serverContext,
        errors,
      });

      // eslint-disable-next-line prefer-promise-reject-errors
      const res = {
        assetGroup,
        originalAssetGroup,
        ...getErrorsPlaceholders(),
        serverErrors,
      };
      if (isEmpty(serverErrors)) {
        return Promise.resolve(res);
      }
      return Promise.reject(res);
    }

    // Activity
    // eslint-disable-next-line no-console
    console.warn(`Asset group bulk upsert system error: ${unexpectedBulkErrorToString(bulkOrSystemError)}`);
    // eslint-disable-next-line prefer-promise-reject-errors
    return Promise.reject({
      assetGroup: assetGroupViewModel,
      originalAssetGroup: originalAssetGroupViewModel,
      ...getErrorsPlaceholders(),
      serverErrors: [genericApiError],
    });
  });
